import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import * as Clipboard from "expo-clipboard";
import * as Sharing from "expo-sharing";
import { useAtomValue } from "jotai";
import { useCallback } from "react";
import { useFragment } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  CopyIcon,
  ShareIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { HStack } from "@whoosh/design-system/src/components/primitive/Stack/HStack";

import { bookingSummaryAnalyticsDataAtom } from "../../../../atoms/analytics/bookingSummaryAnalyticsDataAtom";
import { useShareBooking } from "../../hooks/useShareBooking/useShareBooking";
import { InviteActions_Booking$key } from "./__generated__/InviteActions_Booking.graphql";

export const InviteActions = ({
  clubSlug,
  bookingRef,
}: {
  clubSlug: string | null;
  bookingRef: InviteActions_Booking$key | null;
}) => {
  const booking = useFragment(InviteActionsFragmentString, bookingRef);

  const shareUrl = booking?.shareUrl;

  const { handleShareBooking, isSharing } = useShareBooking();

  const { showToast } = useToast();
  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();
  const handleSharing = useCallback(async () => {
    if (shareUrl) {
      Sharing.shareAsync(shareUrl);
    } else if (booking?.id) {
      handleShareBooking({
        input: {
          bookingId: booking.id,
        },
        onSuccessCallback: (shareUrl) => {
          Sharing.shareAsync(shareUrl);
          trackEvent(
            "BookingDetail.ShareBookingViaShareApi",
            analyticsData ?? {}
          );
        },
      });
    }
  }, [analyticsData, booking?.id, handleShareBooking, shareUrl, trackEvent]);

  const copyToClipboard = useCallback(async () => {
    if (shareUrl) {
      await Clipboard.setStringAsync(shareUrl);
    } else if (booking?.id) {
      handleShareBooking({
        input: {
          bookingId: booking.id,
        },
        onSuccessCallback: (shareUrl) => {
          Clipboard.setStringAsync(shareUrl);
          trackEvent(
            "BookingDetail.ShareBookingViaClipboard",
            analyticsData ?? {}
          );
        },
      });
    }
    showToast({
      semanticStyle: "success",
      title: t`Link copied to clipboard`,
      description: t`Nice! I always wanted to meet your friends.`,
    });
  }, [
    analyticsData,
    booking?.id,
    handleShareBooking,
    shareUrl,
    showToast,
    trackEvent,
  ]);

  return (
    <HStack gap={2} paddingTop={4} position="relative" width="100%">
      <Box flex={1}>
        <Button
          size="tiny"
          variant="secondary"
          leftIcon={<ShareIcon />}
          onPress={handleSharing}
          isLoading={isSharing}
          accessibilityLabel={t`Share button`}
          width="100%"
        >{t`Share`}</Button>
      </Box>
      <Box flex={1}>
        <Button
          accessibilityLabel={t`Copy Link button`}
          size="tiny"
          variant="secondary"
          onPress={copyToClipboard}
          leftIcon={<CopyIcon />}
          isLoading={isSharing}
          testID="invite-actions-copy-link-button"
          width="100%"
        >
          {t`Copy Link`}
        </Button>
      </Box>
    </HStack>
  );
};

const InviteActionsFragmentString = graphql`
  fragment InviteActions_Booking on Booking {
    id
    shareUrl
  }
`;
